/**
 * Notification Listener Hook
 *
 * Subscribes to the 'notifications' collection in Firestore.
 * - Filters by Location ID.
 * - Filters by Target Role (sales vs yard).
 * - Filters by the shared visibility rule (settings + page access; see
 *   utils/notificationVisibility).
 * - Manages "Unread" count.
 * - Handles "Mark as Read".
 * - Triggers browser notifications/toasts for NEW items.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  updateDoc,
  doc,
  arrayUnion
} from 'firebase/firestore';
import { useFirebaseStore } from '../store/firebaseStore';
import type { NotificationItem, NotificationSource, TargetRole } from '@/types/notification.types';
import { useFirestoreCollection } from './firestore/useFirestoreCollection';
import { getUserNotificationsCollectionPath } from '../config/collections';
import {
  buildMarkAllReadPaths,
  getNotificationDocPath,
  mergeBellNotifications,
  resolveMarkReadSource,
} from '../utils/notificationMerge';
import { logger } from '../utils/logger';
import { getNotificationQueryLocations, isNotificationVisibleToUser } from '../utils/notificationVisibility';
import { parseNotificationDoc, parsePersonalNotificationDoc } from '../utils/notificationHistory';

/** Newest notifications kept live in the bell. Older ones live in the history view. */
const BELL_LIMIT = 20;

interface UseNotificationsOptions {
  role?: TargetRole; // Optional when global=true
  global?: boolean; // If true, fetches all roles (yard, sales, all) for global notification display
  enableBrowserNotifications?: boolean; // Whether to show system notifications
  onNewNotification?: (notification: NotificationItem) => void; // Callback for toasts
  enabled?: boolean; // Whether to enable notifications (e.g., based on user permissions)
}

export const useNotifications = ({
  role,
  global = false,
  enableBrowserNotifications = false,
  onNewNotification,
  enabled = true
}: UseNotificationsOptions) => {
  const { db, selectedLocation, currentUser } = useFirebaseStore();
  // Raw items (role-filtered only). Visibility is applied at render time below so
  // settings/permission changes take effect without re-subscribing.
  const [rawNotifications, setRawNotifications] = useState<NotificationItem[]>([]);
  // Personal (single-user) notifications: second source, merged into the bell below
  const [rawPersonal, setRawPersonal] = useState<NotificationItem[]>([]);
  const isFirstLoad = useRef(true);
  const onNewNotificationRef = useRef(onNewNotification);
  const shownNotificationIds = useRef<Set<string>>(new Set());
  const currentUserRef = useRef(currentUser);

  // Extract primitive dependencies to prevent re-subscription on object reference changes
  const userEmail = currentUser?.email;

  // Keep refs updated
  useEffect(() => {
    onNewNotificationRef.current = onNewNotification;
  }, [onNewNotification]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Request notification permission on mount if enabled
  useEffect(() => {
    if (enableBrowserNotifications && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [enableBrowserNotifications]);

  // Subscribed locations the user can still access (falls back to the selected
  // location for users with no v3 notification settings).
  const subscribedLocations = useMemo(() => {
    return currentUser ? getNotificationQueryLocations(currentUser, selectedLocation) : null;
  }, [currentUser, selectedLocation]);

  const subscribedLocationsKey = subscribedLocations?.join(',') ?? '';
  const subscribedLocationsRef = useRef(subscribedLocations);
  useEffect(() => {
    subscribedLocationsRef.current = subscribedLocations;
  }, [subscribedLocations]);

  // Personal notifications listener (generic hook; not location-scoped, so it
  // runs even when the user has no broadcast locations to query).
  const personalPath = enabled && userEmail ? getUserNotificationsCollectionPath(userEmail) : null;
  const parsePersonal = useCallback(
    (id: string, data: Record<string, unknown>) => parsePersonalNotificationDoc(id, data, userEmail || ''),
    [userEmail]
  );
  useFirestoreCollection<NotificationItem>({
    db,
    collectionPath: personalPath,
    queryConstraints: [orderBy('timestamp', 'desc'), limit(BELL_LIMIT)],
    parseDocument: parsePersonal,
    onDataLoaded: setRawPersonal,
    enabled: !!personalPath,
  });
  useEffect(() => {
    if (!personalPath) setRawPersonal([]);
  }, [personalPath]);

  useEffect(() => {
    const locations = subscribedLocationsRef.current;
    if (!enabled || !db || !locations || locations.length === 0 || !userEmail) {
      setRawNotifications([]);
      return;
    }

    // Reset isFirstLoad when effect re-runs (subscribed locations change, etc.)
    // This ensures the first snapshot doesn't trigger toasts
    isFirstLoad.current = true;

    // Query: Location match + newest BELL_LIMIT
    // targetRole is filtered client-side to avoid Firestore disjunction limits.
    // Two 'in' clauses create a cross-product (locations × roles) that can exceed
    // Firestore's 30-disjunction max when there are many locations.
    const targetRoles = global ? ['yard', 'sales', 'all'] : [role || 'sales', 'all'];
    const q = query(
      collection(db, 'notifications'),
      where('locationId', 'in', locations),
      orderBy('timestamp', 'desc'),
      limit(BELL_LIMIT)
    );

    logger.firestore('Subscribe', `notifications (Locs: ${locations.join(',')}, Roles: ${targetRoles.join(',')}, Limit: ${BELL_LIMIT})`);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const source = snapshot.metadata.fromCache ? 'cache' : 'server';
      const changes = snapshot.docChanges().length;
      logger.firestore('Snapshot', `notifications (${snapshot.size} docs, ${changes} changes, from ${source})`);

      // Filter by targetRole client-side (moved out of query to avoid disjunction limit)
      const items = snapshot.docs
        .filter((docSnapshot) => targetRoles.includes(docSnapshot.data().targetRole))
        .map((docSnapshot) => parseNotificationDoc(docSnapshot.id, docSnapshot.data()));
      setRawNotifications(items);

      // Handle NEW notifications (added since last snapshot)
      if (!isFirstLoad.current) {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const data = change.doc.data();
            const notificationId = change.doc.id;

            // Filter by targetRole client-side
            if (!targetRoles.includes(data.targetRole)) return;

            // Skip if I already read it (unlikely on add, but safe)
            if (data.readBy?.includes(userEmail)) return;

            // Skip if we've already shown this notification (prevents duplicates across location changes)
            if (shownNotificationIds.current.has(notificationId)) {
              logger.log(`[Notifications] Skipping duplicate notification ${notificationId} (already shown)`);
              return;
            }

            const newItem = parseNotificationDoc(notificationId, data);

            // Track that we've shown this notification
            shownNotificationIds.current.add(notificationId);

            // Check the shared visibility rule (read via ref to avoid dependency changes)
            const viewer = currentUserRef.current;
            if (!viewer || !isNotificationVisibleToUser(newItem, viewer)) {
              logger.log(`[Notifications] Filtering out notification ${notificationId} (not visible to user)`);
              return;
            }

            // Trigger Callback (for In-App Toast) via Ref
            if (onNewNotificationRef.current) {
              onNewNotificationRef.current(newItem);
            }

            // NOTE: Browser push notifications are handled by FCM (Firebase Cloud Messaging).
            // Do NOT create a duplicate Notification here - FCM already sends the push.
          }
        });
      }

      isFirstLoad.current = false;
    });

    return () => {
      logger.firestore('Unsubscribe', 'notifications');
      unsubscribe();
    };
  }, [db, subscribedLocationsKey, userEmail, role, global, enableBrowserNotifications, enabled]);

  // Apply the shared visibility rule (settings + page access),
  // then merge in the visible personal notifications (newest first, capped).
  const notifications = useMemo(() => {
    if (!currentUser) return [];
    const broadcast = rawNotifications.filter((n) => isNotificationVisibleToUser(n, currentUser));
    const personal = rawPersonal.filter((n) => isNotificationVisibleToUser(n, currentUser));
    return mergeBellNotifications(broadcast, personal, BELL_LIMIT);
  }, [rawNotifications, rawPersonal, currentUser]);

  const unreadCount = useMemo(() => {
    if (!userEmail) return 0;
    return notifications.filter((n) => !n.readBy.includes(userEmail)).length;
  }, [notifications, userEmail]);

  // id -> source of everything currently in the bell, so mark-read hits the right doc
  const sourceById = useMemo(() => {
    const map = new Map<string, NotificationSource>();
    for (const n of notifications) map.set(n.id, n.source === 'personal' ? 'personal' : 'broadcast');
    return map;
  }, [notifications]);

  // Mark a single notification as read. Source is looked up from the bell contents
  // unless passed explicitly (e.g. a toast built from an FCM push).
  const markAsRead = useCallback(async (notificationId: string, source?: NotificationSource) => {
    if (!db || !currentUser) return;
    const resolved = resolveMarkReadSource(notificationId, sourceById, source);
    const path = getNotificationDocPath(notificationId, resolved, getUserNotificationsCollectionPath(currentUser.email));
    const ref = doc(db, path);
    logger.firestore('Write', `${path} (markAsRead)`);
    await updateDoc(ref, {
      readBy: arrayUnion(currentUser.email)
    });
  }, [db, currentUser, sourceById]);

  // Mark all current notifications as read (broadcast + personal)
  const markAllAsRead = useCallback(async () => {
    if (!db || !currentUser || notifications.length === 0) return;

    // Only unread ones, to save writes
    const paths = buildMarkAllReadPaths(
      notifications,
      currentUser.email,
      getUserNotificationsCollectionPath(currentUser.email)
    );
    if (paths.length === 0) return;

    // One batch per source so a failure in one (e.g. personal) can't block the other
    const { writeBatch } = await import('firebase/firestore');
    const personalPrefix = `${getUserNotificationsCollectionPath(currentUser.email)}/`;
    const groups = [
      { label: 'personal', paths: paths.filter((p) => p.startsWith(personalPrefix)) },
      { label: 'broadcast', paths: paths.filter((p) => !p.startsWith(personalPrefix)) },
    ].filter((g) => g.paths.length > 0);

    const results = await Promise.allSettled(
      groups.map(async ({ label, paths: groupPaths }) => {
        const batch = writeBatch(db);
        groupPaths.forEach((path) => {
          batch.update(doc(db, path), { readBy: arrayUnion(currentUser.email) });
        });
        logger.firestore('Write', `${label} notifications (markAllAsRead batch: ${groupPaths.length} docs)`);
        await batch.commit();
      })
    );
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        logger.error(`[Notifications] markAllAsRead failed for ${groups[i].label} batch:`, r.reason);
      }
    });
  }, [db, currentUser, notifications]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
  };
};
